'use client'

import { getTasks, Tasks } from '@/app/queries'
import { TaskThumbnail } from '@/components/task-thumbnail'
import { TaskFiltersComponent } from '@/components/task-filters'
import { ThemeToggle } from '@/components/theme-toggle'
import { ScrollArea } from '@/components/ui/scroll-area'
import { GRID_COLORS } from '@/lib/arc'
import { cn } from '@/lib/utils'
import { useInfiniteScroll } from '@/lib/hooks/use-infinite-scroll'
import type { TaskFilters } from '@/types/filters'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useState, useRef, useCallback } from 'react'

const ListSkeleton = () => {
	return (
		<>
			{[...Array(10)].map((_, i) => (
				<div
					key={i}
					className='flex flex-col space-y-2 font-mono h-16 bg-primary/10 animate-pulse w-full mb-2'
				></div>
			))}
		</>
	)
}

const LoadMoreSkeleton = () => {
	return (
		<>
			{[...Array(5)].map((_, i) => (
				<div
					key={i}
					className='flex flex-col space-y-2 font-mono h-16 bg-primary/10 animate-pulse w-full mb-2'
				></div>
			))}
		</>
	)
}

export function Menu() {
	const pathname = usePathname()
	const id = pathname.split('/').pop()
	const [tasks, setTasks] = useState<Tasks>([])
	const [total, setTotal] = useState(0)
	const [loading, setLoading] = useState(true)
	const [loadingMore, setLoadingMore] = useState(false)
	const [hasMore, setHasMore] = useState(true)
	const [filters, setFilters] = useState<TaskFilters>({})
	const currentOffsetRef = useRef(0)

	const loadTasksInternal = async (
		currentFilters: TaskFilters,
		reset = false
	) => {
		if (reset) {
			setLoading(true)
			currentOffsetRef.current = 0
		} else {
			setLoadingMore(true)
		}

		try {
			const offsetToUse = reset ? 0 : currentOffsetRef.current
			const filtersWithPagination = {
				...currentFilters,
				offset: offsetToUse,
				limit: 50,
			}

			const result = await getTasks(filtersWithPagination)

			if (reset) {
				setTasks(result.data)
			} else {
				setTasks((prev) => [...prev, ...result.data])
			}

			currentOffsetRef.current = result.nextOffset
			setTotal(result.total)
			setHasMore(result.hasMore)
		} catch (error) {
			console.error('Failed to load tasks:', error)
		} finally {
			setLoading(false)
			setLoadingMore(false)
		}
	}

	const loadMore = () => {
		if (!loadingMore && hasMore) {
			loadTasksInternal(filters, false)
		}
	}

	const scrollRef = useInfiniteScroll({
		hasMore,
		loading: loadingMore,
		onLoadMore: loadMore,
		threshold: 200,
	})

	// Load tasks when filters change
	useEffect(() => {
		loadTasksInternal(filters, true)
	}, [filters])

	const handleFiltersChange = useCallback((newFilters: TaskFilters) => {
		setFilters(newFilters)
	}, [])

	return (
		<>
			<div className='flex flex-row justify-between items-center p-2'>
				<p className='text-2xl text-primary font-mono'>
					{loading ? '...' : total.toLocaleString()} tasks
				</p>
				<ThemeToggle />
			</div>

			<TaskFiltersComponent
				filters={filters}
				onFiltersChange={handleFiltersChange}
			/>

			<ScrollArea className='flex flex-col space-y-2 text-xs font-mono h-full'>
				<div ref={scrollRef}>
					{loading ? (
						<ListSkeleton />
					) : (
						<>
							{tasks.map((task) => (
								<Link
									href={`/${task.id}`}
									key={task.id}
									className={cn(
										'flex flex-row gap-4 py-2 px-4 hover:bg-accent',
										task.id === id && 'bg-primary/10'
									)}
								>
									<div className='flex flex-col gap-1'>
										<div className='flex flex-col line-clamp-1 w-[150px]'>
											<p className='text-[10px] text-primary/60 font-mono'>
												{task.dataset}
											</p>
											<p className='text-sm text-primary font-mono'>
												{task.taskId}
											</p>
										</div>
										<p className='text-xs text-primary/60 font-mono'>
											ex: {task.train_count}
										</p>
										<div className='flex flex-row gap-[1px]'>
											{task.colors.map((c: string) => (
												<div
													key={c}
													style={{
														width: 7,
														height: 7,
														backgroundColor: GRID_COLORS.find(
															(color) => color[1] === parseInt(c)
														)?.[2],
													}}
												/>
											))}
										</div>
									</div>
									<TaskThumbnail task={task} />
								</Link>
							))}
							{loadingMore && <LoadMoreSkeleton />}
							{!hasMore && tasks.length > 0 && (
								<div className='text-center py-4 text-primary/60 font-mono text-xs'>
									No more tasks to load
								</div>
							)}
						</>
					)}
				</div>
			</ScrollArea>
		</>
	)
}
